import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { B13, B13Ink, B13TypeScale } from "../../../theme/b13Tokens";
import { surfaceSx, primaryBtnSx, secondaryBtnSx } from "../shared/moduleSx";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useFormik } from "formik";
import {
  useCreateCourseMaterialMutation,
  useCreateCourseMutation,
  useDeleteCourseMaterialMutation,
  useGetCourseQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { StepUpSpinner } from "../../../Components/Loader";
import { formatDateOnly } from "../../../utils/dateOnly";
import CourseFormBody, {
  CourseFormBodyHandle,
  CourseFormValues,
  DEFAULT_COURSE_FORM_VALUES,
} from "../shared/CourseFormBody";

/**
 * Course edit page.
 *
 * The backend supports edits via CourseController::store when an `id` is
 * present in the payload — we reuse the same mutation. Attachments are
 * managed live through the course-material endpoints; the video URL is
 * upserted here as a single "hero" course_material row of type=video
 * (orderNumber=1, matching the design decision to not add a video_url
 * column on courses).
 *
 * Unsaved-changes guard uses react-router's unstable_useBlocker (available
 * in the version this app ships) with a confirmation dialog. If a newer
 * router lands the hook name may change; we keep the fallback in mind.
 */

type CourseWire = {
  id: number;
  courseName: string;
  courseCode?: string | null;
  description?: string | null;
  status?: string | null;
  thumbnail?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  gradeId?: number | null;
  subjectId?: number | null;
  staffId?: number | null;
  materials?: Array<{
    id: number;
    title?: string | null;
    materialType?: string | null;
    filePath?: string | null;
    externalUrl?: string | null;
    orderNumber?: number | null;
  }>;
};

const CourseEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showErrorMessage, showMessage } = useNotifier();
  const bodyRef = useRef<CourseFormBodyHandle | null>(null);

  const { data, isLoading, refetch } = useGetCourseQuery({ id: id ?? "" });
  const [updateCourse, { isLoading: saving }] = useCreateCourseMutation();
  const [createMaterial] = useCreateCourseMaterialMutation();
  const [deleteMaterial] = useDeleteCourseMaterialMutation();

  const course: CourseWire | null = useMemo(() => {
    const arr = Array.isArray(data?.data) ? (data!.data as CourseWire[]) : [];
    return arr.find((c) => String(c.id) === String(id)) ?? null;
  }, [data, id]);

  const initialVideoUrl = useMemo(() => {
    const v = (course?.materials ?? []).find(
      (m) => (m.materialType || "").toLowerCase() === "video",
    );
    return v?.externalUrl ?? "";
  }, [course]);

  const formik = useFormik<CourseFormValues>({
    initialValues: DEFAULT_COURSE_FORM_VALUES,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!course) return;
      const payload = {
        id: course.id,
        courseName: values.courseName.trim(),
        courseCode: values.courseCode.trim() || null,
        description: values.description,
        gradeId: values.gradeId || null,
        subjectId: values.subjectId || null,
        staffId: values.staffId || null,
        status: values.status || "draft",
        startDate: values.startDate || null,
        endDate: values.endDate || null,
        thumbnail: values.thumbnail || null,
      };
      try {
        const res = await updateCourse(payload).unwrap();
        if (!res.status) {
          showErrorMessage(res.message || "Failed to save course.");
          return;
        }

        // Swap the video "hero" material row. The course-material endpoint
        // has no upsert semantic, so we run a create-then-delete when the
        // URL changed — order matters:
        //   1) create the new row FIRST
        //   2) only delete the old row if the create succeeded
        // This guarantees the course is never left in a state worse than
        // when the save started. If the create fails, the old row stays
        // and the user sees an explicit error toast. If the create
        // succeeds but the delete fails, the user sees two video rows
        // and can delete one — data preserved.
        //
        // Empty new URL is treated as an explicit "clear" only when a
        // previous row existed; nothing to do when both are empty.
        const trimmed = values.videoUrl.trim();
        const existing = (course.materials ?? []).find(
          (m) => (m.materialType || "").toLowerCase() === "video",
        );
        const existingUrl = existing?.externalUrl ?? "";

        if (trimmed !== existingUrl) {
          let newRowCreated = false;

          if (trimmed) {
            try {
              const created = await createMaterial({
                courseId: course.id,
                title: "Video",
                materialType: "video",
                externalUrl: trimmed,
                orderNumber: 1,
                isPublished: true,
              }).unwrap();
              newRowCreated = !!created?.status;
              if (!newRowCreated) {
                showErrorMessage(
                  "Course was saved but the video URL could not be recorded. The previous video (if any) was kept.",
                );
              }
            } catch {
              showErrorMessage(
                "Course was saved but the video URL could not be recorded. The previous video (if any) was kept.",
              );
            }
          }

          // Only remove the old row when we're sure it has been superseded
          // — either by a successful create, or by an explicit clear
          // (trimmed === "" and existing existed).
          const safeToDelete =
            !!existing && (newRowCreated || trimmed === "");
          if (safeToDelete) {
            try {
              await deleteMaterial(String(existing!.id)).unwrap();
            } catch {
              // Non-fatal: user will see two video rows and can remove one.
              showErrorMessage(
                "New video was saved but the previous video row could not be deleted. You can remove it from the attachments list.",
              );
            }
          }
        }

        showMessage(res.message || "Course updated.");
        formik.resetForm({ values }); // marks the form as clean
        refetch();
        navigate("/course/list");
      } catch {
        showErrorMessage("Failed to save course.");
      }
    },
  });

  // Seed formik once course data arrives.
  useEffect(() => {
    if (!course) return;
    formik.resetForm({
      values: {
        id: course.id,
        courseName: course.courseName ?? "",
        courseCode: course.courseCode ?? "",
        description: course.description ?? "",
        gradeId: course.gradeId != null ? String(course.gradeId) : "",
        subjectId: course.subjectId != null ? String(course.subjectId) : "",
        staffId: course.staffId != null ? String(course.staffId) : "",
        status: course.status ?? "draft",
        startDate: formatDateOnly(course.startDate ?? ""),
        endDate: formatDateOnly(course.endDate ?? ""),
        thumbnail: course.thumbnail ?? "",
        videoUrl: initialVideoUrl,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course, initialVideoUrl]);

  // Unsaved-changes guard. This app uses BrowserRouter (not the data
  // router), so react-router's useBlocker isn't available. We cover the
  // three real exit paths explicitly:
  //   1) tab close / reload  → window.beforeunload
  //   2) in-app back or cancel → local confirmation dialog before navigate()
  //   3) sidebar navigation → not blockable without the data router; the
  //      beforeunload event doesn't fire for internal navigation. Users
  //      have to click the back arrow or Cancel to hit the confirmation.
  const dirty = formik.dirty && !saving;
  const [pendingNav, setPendingNav] = useState<string | null>(null);

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const attemptNav = (to: string) => {
    if (dirty) setPendingNav(to);
    else navigate(to);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <StepUpSpinner size="large" label="Loading course" />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Course not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          sx={{ ...secondaryBtnSx, mt: 2 }}
          variant="outlined"
          onClick={() => attemptNav("/course/list")}
        >
          Back to courses
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <Box sx={{ ...surfaceSx, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={() => attemptNav("/course/list")}
            sx={{ color: B13Ink.ink3, "&:hover": { color: B13.primary } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography sx={B13TypeScale.h2}>Edit course</Typography>
            <Typography sx={{ ...B13TypeScale.body, mt: 0.25 }}>
              {course.courseName}
              {course.courseCode ? ` · ${course.courseCode}` : ""}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <CourseFormBody
        ref={bodyRef}
        mode="edit"
        formik={formik}
        submitting={saving}
        submitLabel={saving ? "Saving…" : "Save changes"}
        courseId={course.id}
        existingMaterials={course.materials ?? []}
        onMaterialsChanged={refetch}
      />

      {/* Unsaved-changes confirmation (in-app navigation from Cancel/Back) */}
      <Dialog
        open={!!pendingNav}
        onClose={() => setPendingNav(null)}
        PaperProps={{ sx: { borderRadius: 2, border: `1px solid ${B13.line}` } }}
      >
        <DialogTitle sx={{ ...B13TypeScale.h3 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={B13TypeScale.body}>
            You have unsaved changes to this course. If you leave now, your edits will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setPendingNav(null)}
            sx={secondaryBtnSx}
          >
            Keep editing
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const to = pendingNav;
              setPendingNav(null);
              if (to) navigate(to);
            }}
            sx={{ ...primaryBtnSx, bgcolor: "#E11D48", "&:hover": { bgcolor: "#E11D48", filter: "brightness(0.95)", boxShadow: "none" } }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseEdit;
